import { useDebouncedValue } from '@funkit/connect-core'
import { useEffect, useMemo, useState } from 'react'

import { useFunkitBranding } from '../branding/BrandingContext'
import { isBelowMinimum } from '../hooks/useGuardedAmountInput'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { DepositAmountScreen } from '../screens/DepositAmountScreen'
import type { FeeDetailsRow } from '../screens/FeeDetailsScreen'
import { formatPercent, formatUsd, groupThousands } from '../utils/formatAmount'
import type {
  DepositAmountEntryConfig,
  DepositFlowCallbacks,
  DepositInitialAmount,
  DepositMethod,
} from './depositFlowConfig'
import { accountBalanceChipProps } from './accountContext'
import { resolveDepositRailCopy } from './depositRailCopy'
import { FeeDetailsSheet, useFeeDetailsSheet } from './FeeDetailsSheet'

/** The presets shown when the host doesn't curate its own. */
const DEFAULT_QUICK_AMOUNTS = ['10', '20', '50', '100']

// A keystroke invalidates the shown fee at once; it reprices only after the
// input has held still this long. Fee disclosure only — nothing waits on it.
const AMOUNT_DEBOUNCE_MS = 300

/**
 * Container for the V2 amount-entry screen: owns the typed amount, prices the
 * fee from the configured rate and hands submission to the host. The payment
 * rail behind the submit is not part of this step — `onSubmitAmount` is the
 * seam it will attach to.
 */
export function DepositAmountStep({
  config,
  method,
  callbacks,
  initialAmount,
  lockAmount,
}: {
  config: DepositAmountEntryConfig
  /** The method the submit stands for; it words the button and the drawer. */
  method?: DepositMethod
  callbacks: DepositFlowCallbacks
  /** Prefills the typed amount. */
  initialAmount?: DepositInitialAmount
  /**
   * Locks the prefilled amount against edits: typing, backspace/clear, and
   * quick-amount taps all reject.
   */
  lockAmount?: boolean
}) {
  const { t, locale } = useFunkitTranslation()
  const { hideAttribution } = useFunkitBranding()
  const { sheet, openFeeDetails } = useFeeDetailsSheet()
  const [input, setInput] = useState(initialAmount?.value ?? '')
  // The input the shown fee prices. It trails the live input while typing, and
  // the fee line is faded out and inert until the two agree.
  const { debounced: settledInput, isSettled } = useDebouncedValue(
    input,
    AMOUNT_DEBOUNCE_MS,
  )
  const feePending = !isSettled

  // A rail that quotes its own fee (`fiat`) leaves this unset.
  const feeRate = config.feeRate ?? 0
  const amountUsd = input === '' ? 0 : Number(input)
  const feeUsd = amountUsd * feeRate
  const settledAmountUsd = settledInput === '' ? 0 : Number(settledInput)
  const settledFeeUsd = settledAmountUsd * feeRate
  // A zero-valued settled entry has no fee to show — it's still the empty state.
  const fee =
    settledAmountUsd > 0
      ? t('depositAmount.fee', { amount: formatUsd(settledFeeUsd) })
      : undefined

  const quickAmountsUsd = config.quickAmountsUsd
  const quickAmounts = useMemo(
    () =>
      (quickAmountsUsd ?? DEFAULT_QUICK_AMOUNTS).map((value) => ({
        value,
        label: `$${groupThousands(value)}`,
      })),
    [quickAmountsUsd],
  )

  const title = config.title ?? t('depositAmount.title')
  const { submitLabel, methodName, methodBadge } = resolveDepositRailCopy(
    config,
    method?.type,
    t,
  )
  // Without the host's `onSubmitAmount` a press would go nowhere; hold the CTA
  // disabled instead of offering a dead button, and flag the misconfiguration.
  const hasSubmitHandler = !!callbacks.onSubmitAmount
  useEffect(() => {
    if (!hasSubmitHandler) {
      logger.warn(
        'connect-rn: amountEntry is configured without onSubmitAmount; the submit CTA stays disabled',
      )
    }
  }, [hasSubmitHandler])
  // A below-floor amount can be entered, so the CTA that would submit it is
  // held closed; the screen swaps in its own action for that state.
  const canSubmit =
    amountUsd > 0 &&
    !isBelowMinimum(input, config.minLimitUsd) &&
    hasSubmitHandler

  const rateLabel = formatPercent(config.feeRate ?? 0)
  // The drawer's figures are a snapshot: the amount screen is behind the overlay
  // while it is open, so nothing under them can change. Settled values, so they
  // agree with the fee line that opened it (which is inert while unsettled).
  const feeRows: FeeDetailsRow[] = [
    {
      label: t('feeDetails.amount'),
      value: formatUsd(settledAmountUsd),
    },
    {
      label: t('feeDetails.fee', { rate: rateLabel }),
      rate: rateLabel,
      value: formatUsd(settledFeeUsd),
    },
    {
      label: t('feeDetails.totalCost'),
      value: formatUsd(settledAmountUsd + settledFeeUsd),
      isTotal: true,
    },
  ]

  const handleSubmit = () => {
    if (!canSubmit) {
      return
    }
    // The decision point: what was asked for and what it costs, before any
    // payment rail is attached.
    logger.info('depositAmountSubmit', { amountUsd: input, feeUsd })
    callbacks.onSubmitAmount?.(input)
  }

  return (
    <>
      <DepositAmountScreen
        title={title}
        brandIcon={config.brandIcon}
        accountContext={accountBalanceChipProps(config.accountContext, locale)}
        input={input}
        onChangeInput={setInput}
        quickAmounts={quickAmounts}
        limitUsd={config.limitUsd}
        minLimitUsd={config.minLimitUsd}
        disabled={lockAmount}
        fee={fee}
        feePending={feePending}
        onPressFee={openFeeDetails}
        feeAccessibilityLabel={t('feeDetails.title')}
        methodBadge={methodBadge}
        submitLabel={submitLabel}
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        amountAccessibilityLabel={t('depositAmount.amountLabel')}
        backspaceAccessibilityLabel={t('depositAmount.delete')}
        showAttribution={!hideAttribution}
      />

      <FeeDetailsSheet
        sheet={sheet}
        rows={feeRows}
        {...(methodName === undefined
          ? {}
          : {
              summary: t('feeDetails.summaryAddedToCharge', {
                method: methodName,
                rate: rateLabel,
              }),
            })}
      />
    </>
  )
}
